import { useContext, useEffect, useRef, useMemo } from "react"
import { useQuery } from "@apollo/client"
import { Formik, Form as FormWrapper, useFormikContext } from "formik"
import _ from "lodash"

// Context
import { UserContext } from "../../context/userContext"
import { FormContext } from "../../context/formContext"

// Hooks
// Comps
import BlockBuilder from "./BlockBuilder"
import FormLoader from "./FormLoader"

// Helpers
import validationSchema from "../../helpers/validationSchema"
import initialValues from "./initialValues"
import { transformPayload } from "../../helpers/dateNormalization"
import { cleanMultiFieldValues } from "../../helpers/cleanMultiFieldValues"
import sanitizeTemplateBlocks from "../../helpers/sanitizeTemplateBlocks"
import { logApolloError } from "../../helpers/apolloErrorLogger"

// Queries
import { GET_TEMPLATE_PAGE } from "./queries"

import styles from "../../styles/Form.module.css"

export default function FormContainer({
  blocks,
  globalValues,
  dataPropagation,
}) {
  const form = useContext(FormContext)
  const userCtx = useContext(UserContext)
  const currentBlock = _.find(blocks, ["index", Number(form.currentPage)])
  const activeBlock = currentBlock || blocks?.[0]
  const isSubmitBlock = activeBlock?.block_name === "submit_block"
  const hasParticipantId = !_.isEmpty(form.participant_id)

  const getTotalFields = () => {
    if (activeBlock) {
      return Number(activeBlock.total_fields)
    }
    return null
  }

  const { loading, data } = useQuery(GET_TEMPLATE_PAGE, {
    skip:
      !userCtx.session.user.staff_id ||
      isSubmitBlock ||
      (form.requiresParticipant && !hasParticipantId),
    fetchPolicy: "no-cache",
    variables: {
      staff_id: userCtx.session.user.staff_id,
      organization_id: userCtx.session.user.organization_id,
      enterprise_id: userCtx.session.user.enterprise_id,
      type: "system",
      participant_id: form.participant_id,
      supervision_staff_membership_id: form.supervision_staff_membership_id,
      page: activeBlock?.index ?? form.currentPage,
      idStamp: form.idStamp,
      idTemplate: form.idTemplate,
      editMode: form.editMode,
    },
    onCompleted: (data) => {
      form.setFormError(null)

      // Sanitize and save fetched block definition
      const sanitizedBlocks = sanitizeTemplateBlocks(
        data?.template?.template_name,
        data?.template?.blocks,
      )
      form.handleFetchedBlock(sanitizedBlocks[0])

      // Save template info
      form.saveTemplateInfo({
        template_title: data?.template?.template_title,
        audit_type: data?.template?.audit_type,
      })
    },
    onError: (error) => {
      logApolloError("Error loading form template page:", error)
      form.setFormError(error)
    },
  })

  const sanitizedBlocks = useMemo(
    () =>
      sanitizeTemplateBlocks(
        data?.template?.template_name,
        data?.template?.blocks,
      ),
    [data?.template?.template_name, data?.template?.blocks],
  )

  const memoizedValidationSchema = useMemo(
    () => validationSchema(sanitizedBlocks || [], "template", form.editMode),
    [sanitizedBlocks, form.editMode],
  )

  const propagationValues = useMemo(() => {
    const block_name = activeBlock?.block_name
    const hasBlocks = Boolean(sanitizedBlocks?.length)
    return block_name && _.has(dataPropagation, block_name) && hasBlocks
      ? transformPayload(dataPropagation[block_name], form.dateFieldDefMap)
      : {}
  }, [
    dataPropagation,
    activeBlock?.block_name,
    sanitizedBlocks,
    form.dateFieldDefMap,
  ])

  const templateReady = Boolean(sanitizedBlocks?.[0]?.fields?.length)

  if (loading || (!templateReady && !isSubmitBlock)) {
    return <FormLoader numberOfFields={getTotalFields()} />
  }

  const fieldDef = sanitizedBlocks?.[0]?.fields || []
  const blockName =
    activeBlock?.block_name || sanitizedBlocks?.[0]?.block_name || ""
  const blockValuesFromGlobal = globalValues[blockName] || {}

  const handleFormValues = () => {
    const hasDataPropagation = !_.isEmpty(propagationValues)

    const baseFormValues = initialValues(data)

    const initialPropagatedValues = () =>
      _.reduce(
        propagationValues,
        (acc, value, key) => {
          const field = _.find(fieldDef, ["field_name", key])

          const isTypeNumber =
            field?.field_type === "number" ||
            field?.field_type === "conditional_number"

          if (isTypeNumber) {
            const minValue = field.field_validation?.number_min ?? null
            const maxValue = field.field_validation?.number_max ?? null
            const hasValidation = _.isNumber(minValue) || _.isNumber(maxValue)

            const handleNumericalValue = () => {
              if (!_.isNumber(value)) return null
              if (hasValidation) {
                return value >= minValue && value <= maxValue ? value : null
              }
              return value
            }

            return { ...acc, [key]: handleNumericalValue() }
          }

          return { ...acc, [key]: value }
        },
        {},
      )

    let mergedValues = {
      ...baseFormValues,
      ...initialPropagatedValues(),
      ...blockValuesFromGlobal,
    }

    // Remove any multi-field blocks that are completely empty
    cleanMultiFieldValues(mergedValues, fieldDef)

    if (!hasDataPropagation) return { ...baseFormValues, ...mergedValues }
    return mergedValues
  }

  const formValues = !isSubmitBlock ? handleFormValues() : {}

  // small helper component (must be inside Formik tree) to sync FormikContext into FormContext
  function FormikContextWatcher() {
    const formik = useFormikContext()
    const debouncedRef = useRef(null)
    const prevRef = useRef(null)

    useEffect(() => {
      // create debounced setter once (recreated only if form.setFormikState changes)
      debouncedRef.current = _.debounce((next) => {
        if (_.isEqual(prevRef.current, next)) return
        const cloned = {
          ...next,
          values: _.cloneDeep(next.values),
          errors: _.cloneDeep(next.errors),
        }
        form.setFormikState?.((prev) =>
          _.isEqual(prev, cloned) ? prev : cloned,
        )
        prevRef.current = cloned
      }, 100) // adjust delay as needed

      return () => {
        debouncedRef.current?.flush?.()
        debouncedRef.current?.cancel?.()
        debouncedRef.current = null
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.setFormikState])

    useEffect(() => {
      const next = {
        isValid: formik.isValid,
        isDirty: formik.dirty,
        values: formik.values,
        errors: formik.errors,
        submitForm: formik.submitForm,
        validateForm: formik.validateForm,
      }

      // schedule the debounced update (deep compare runs inside debounced handler)
      debouncedRef.current?.(next)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.isValid, formik.values, formik.submitForm])

    return null
  }

  return (
    <Formik
      key={blockName}
      initialValues={formValues}
      validationSchema={memoizedValidationSchema}
    >
      <FormWrapper className={styles.form}>
        <FormikContextWatcher />
        <BlockBuilder block={sanitizedBlocks[0] || activeBlock} />
      </FormWrapper>
    </Formik>
  )
}
